import { useCallback, useEffect, useMemo, useRef } from 'react';
import { GridApi, GridOptions, RowSelectionOptions } from 'ag-grid-community';
import { GhostRow, isGhostRow, Row, UseControlledAGGridProps } from '../types';
import { getOnSelectionChanged, toRowSelectionOptions } from '../utils';
import {
  DEFAULT_SELECTION_COLUMN_LABELS,
  SelectionColumnLabels,
} from '../../labels';

export const useSelectable = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>,
  api: GridApi | null
) => {
  const selectable = props.selectable;
  const selectableEnabled = selectable !== undefined;
  const selectableRef = useRef(selectable);
  useEffect(() => {
    selectableRef.current = selectable;
  }, [selectable]);
  const previousSelectedIdsRef = useRef<RowId[]>([]);
  const selectedIds = useMemo(() => {
    if (!selectable) {
      return [] as RowId[];
    }

    if (selectable.mode === 'single') {
      return selectable.id !== null ? [selectable.id] : ([] as RowId[]);
    }

    return selectable.ids;
  }, [selectable]);

  // Ghost rows: minimal placeholder rows for selected IDs that are not in
  // the current rows. These give AG Grid row nodes so selection state is
  // preserved when the parent filters rows out. They are hidden via external
  // filter and carry no real data — the controlled selectedIds are the source
  // of truth.
  const knownRowIds = new Set(props.rows.map(row => row.id));
  const ghostRows: GhostRow<RowId>[] = selectedIds
    .filter(id => !knownRowIds.has(id))
    .map(id => ({
      id,
      data: {},
      isGhost: true,
    }));

  const hasGhostRowsRef = useRef(ghostRows.length > 0);
  hasGhostRowsRef.current = ghostRows.length > 0;

  const externalFilterGridProps: GridOptions<Row<RowData, RowId>> = {
    isExternalFilterPresent: () => hasGhostRowsRef.current,
    doesExternalFilterPass: node => {
      return !isGhostRow(node?.data);
    },
  };

  // Re-evaluate external filter when ghost rows appear or disappear so AG Grid
  // hides/shows them and also re-enables row drag when no ghost rows exist.
  const hasGhostRows = ghostRows.length > 0;
  useEffect(() => {
    if (api) {
      api.onFilterChanged();
    }
  }, [api, hasGhostRows]);

  const rowSelectionKey = JSON.stringify(
    selectable ? toRowSelectionOptions(selectable) : null
  );
  const rowSelection: RowSelectionOptions | undefined = useMemo(
    () => (selectable ? toRowSelectionOptions(selectable) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: rebuild only when the derived options change
    [rowSelectionKey]
  );

  // `rowSelection` is part of `gridOptions`, which AG Grid only reads when the
  // grid is created. Later changes — e.g. `enableRowClickSelection` flipping
  // when an external "select all" takes over the selection — have to be pushed
  // through the API.
  useEffect(() => {
    if (!api) {
      return;
    }
    api.updateGridOptions({ rowSelection });
  }, [api, rowSelection]);

  useEffect(() => {
    if (!api || !selectableEnabled) {
      return;
    }

    if (selectedIds.length === 0) {
      api.deselectAll();
      previousSelectedIdsRef.current = [];
      return;
    }

    // Find newly selected IDs by comparing with previous selection
    const previousSelectedIds = previousSelectedIdsRef.current;
    const newlySelectedIds = selectedIds.filter(
      id => !previousSelectedIds.includes(id)
    );

    // Only scroll if at least one newly selected row exists and none are visible
    if (newlySelectedIds.length > 0) {
      const newlySelectedNodes = newlySelectedIds
        .map(id => api.getRowNode(id))
        .filter(node => node !== undefined);

      // Check if any of the newly selected rows are actually visible in the viewport
      // (not just rendered in the DOM buffer)
      const verticalPixelRange = api.getVerticalPixelRange();
      const anyVisible = newlySelectedNodes.some(node => {
        const rowTop = node.rowTop;
        const rowHeight = node.rowHeight;

        if (
          rowTop === null ||
          rowTop === undefined ||
          rowHeight === null ||
          rowHeight === undefined
        ) {
          return false;
        }

        const rowBottom = rowTop + rowHeight;
        return (
          rowTop < verticalPixelRange.bottom &&
          rowBottom > verticalPixelRange.top
        );
      });

      // Only scroll if none of the newly selected rows are visible
      if (!anyVisible && newlySelectedNodes[0]) {
        api.ensureNodeVisible(newlySelectedNodes[0]);
      }
    }

    // Deselect rows that were previously selected but are no longer in selectedIds.
    // Only toggle nodes whose state actually differs: a redundant setSelected call
    // moves AG Grid's selection anchor (the "root" used for shift-click range
    // selection), which would break range selection driven from the grid itself.
    for (const id of previousSelectedIds) {
      if (!selectedIds.includes(id)) {
        const rowNode = api.getRowNode(id);
        if (rowNode?.isSelected()) {
          rowNode.setSelected(false);
        }
      }
    }

    // Select rows that should be selected (including ghost rows which have row nodes)
    for (const selectedId of selectedIds) {
      const rowNode = api.getRowNode(selectedId);
      if (rowNode && !rowNode.isSelected()) {
        rowNode.setSelected(true);
      }
    }

    // Update the previous selection for next comparison
    previousSelectedIdsRef.current = selectedIds;
    // props.rows is intentionally included: when rows change (e.g. ghost rows
    // replaced by real rows after clearing a filter), AG Grid's tree restructuring
    // can lose selection state. Re-running the effect restores it.
  }, [api, selectableEnabled, selectedIds, props.rows]);

  // Stable wrapper read by the selection column's cell renderer. It delegates
  // to the live ref rather than closing over `selectable`, because AG Grid
  // caches whatever it is handed and a captured callback would go stale.
  const getRowLabel = useCallback((row: unknown) => {
    const current = selectableRef.current;
    return current?.mode === 'multiple'
      ? current.getRowLabel?.(row as RowData)
      : undefined;
  }, []);

  // Only name the header ourselves when AG Grid's select-all checkbox is
  // hidden. When it is shown, its own aria-label already names the column, and
  // adding ours just concatenates the two into "Select all rows Select".
  const selectionColumnHeaderLabel: string | undefined = useMemo(() => {
    if (selectable?.mode !== 'multiple' || !selectable.hideHeaderCheckbox) {
      return undefined;
    }
    const labels: SelectionColumnLabels = {
      ...DEFAULT_SELECTION_COLUMN_LABELS,
      ...props.selectionColumnLabels,
    };
    return labels.header;
  }, [selectable, props.selectionColumnLabels]);

  const selectionGridProps: GridOptions<Row<RowData, RowId>> | undefined =
    selectable
      ? {
          rowSelection,
          onSelectionChanged: getOnSelectionChanged<RowData, RowId>(
            selectableRef
          ),
        }
      : undefined;

  const selectState = selectableEnabled ? { rowSelection: selectedIds } : {};

  return {
    selectState,
    selectionGridProps,
    getRowLabel,
    selectionColumnHeaderLabel,
    ghostRows,
    externalFilterGridProps,
  };
};
